// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import {
  Button, Card, CardHeader, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, List, ListItem, ListItemIcon, ListItemText, makeStyles, Typography,
} from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import { IsEmpty } from '../../../common/checks';
import { Status } from '../../../common/constants';

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width:           '100%',
    height:          230,
    backgroundColor: theme.palette.background.paper,
    overflow:        'auto',
  },
}));

const OutletsManagementDialog = ({ open, onClose, pageId, loyaltyCardId, onRefresh }) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);

  const getIndex = (list, outlet) => list.findIndex((o) => o.id === outlet.id);
  const not = (a, b) => a.filter((item) => getIndex(b, item) === -1);
  const intersection = (a, b) => a.filter((item) => getIndex(b, item) !== -1);
  const union = (a, b) => [...a, ...not(b, a)];

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const getLoyaltyOutlets = async () => {
    try {
      const availableOutlets = await apiService.getPageOutlets(pageId, { status: Status.Live });
      const { rows: participateOutlets } = await apiService.getParticipateOutlets(pageId, loyaltyCardId);

      setLeft(not(availableOutlets, participateOutlets));
      setRight(participateOutlets);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (value) => () => {
    const currentIndex = getIndex(checked, value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setChecked(not(checked, leftChecked));
    setLeft(not(left, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const updatedPOs = await apiService.updateLoyaltyCardParticipateOutlets({
        pageId, loyaltyCardId, outletIds: right.map((outlet) => outlet.id),
      });

      toast.success('Successfully updated participating outlets');

      onRefresh(updatedPOs);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const dataList = (title, items) => (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={(
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
          />
        )}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list">
        {items.map((outlet) => (
          <ListItem key={outlet.id} role="listitem" button onClick={handleToggle(outlet)}>
            <ListItemIcon>
              <Checkbox
                checked={getIndex(checked, outlet) !== -1}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText
              id={`outlet-${outlet.id}`}
              primary={`${outlet.id} - ${!IsEmpty(outlet.brand) ? outlet.brand : outlet.name}`}
              secondary={`${outlet.address} ${!IsEmpty(outlet.unitNo) ? `#${outlet.unitNo}` : ''} Singapore ${outlet.postalCode}`}
            />
          </ListItem>
        ))}
      </List>
    </Card>
  );

  useEffect(() => {
    getLoyaltyOutlets();
  }, []);

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      {isLoading && <OverLoading />}
      <DialogTitle>
        <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>Manage Participating Outlets</Typography>
      </DialogTitle>
      <DialogContent>
        <div className="w-100 mb-4">
          <Grid
            container
            justifycontent="flex-start"
            alignItems="center"
            className="m-auto"
          >
            <Grid xs={5} className="p-0" item>
              {dataList(`Available Outlets`, left)}
            </Grid>
            <Grid xs={2} item>
              <Grid container direction="column" alignItems="center">
                <Button
                  variant="outlined"
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0}
                >
                  {'>'}
                </Button>
                <div className="mb-2" />
                <Button
                  variant="outlined"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                >
                  {'<'}
                </Button>
              </Grid>
            </Grid>
            <Grid xs={5} className="p-0" item>
              {dataList('Participating Outlets', right)}
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="d-flex p-3">
          <Button
            variant="contained"
            className="mr-2"
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
          <Button variant="text" onClick={onClose}>Cancel</Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

OutletsManagementDialog.propTypes = {
  open:          PropTypes.bool.isRequired,
  onClose:       PropTypes.func.isRequired,
  onRefresh:     PropTypes.func.isRequired,
  pageId:        PropTypes.number.isRequired,
  loyaltyCardId: PropTypes.number.isRequired,
};

export default OutletsManagementDialog;
